<template>
  <div class="warehouse-manage">
    <div class="content-wrapper">
      <div class="title">
        <span>入库管理</span>
        <Button
          class="add-btn"
          @click="showInbound"
        >新增入仓</Button>
      </div>
      <div class="tool-wrapper">
        <div class="menu-box">
          <div
            v-for="(tab, index) in tabs"
            :key="`tab${index}`"
            class="menu-item"
            :class="[activeTab == tab.value ? 'active' : '']"
            @click="switchTab(tab.value)"
          >
            {{ tab.name }}
          </div>
        </div>
        <div class="search-box">
          <input
            v-model="form_data.keyword"
            type="text"
            :placeholder="searchPlaceholder"
            @keyup.enter="onSearch"
          />
          <div class="search-btn" @click="onSearch">搜索</div>
        </div>
      </div>

      <!-- 出入库明细筛选：类型 + 操作时间范围 -->
      <div v-if="activeTab == 'log'" class="log-filter">
        <div class="filter-item">
          <span class="filter-label">类型</span>
          <Select
            class="filter-select"
            :value="log_filter.type"
            @on-change="onLogTypeChange"
          >
            <Option :value="0">全部</Option>
            <Option :value="1">入库</Option>
            <Option :value="2">出库</Option>
          </Select>
        </div>
        <div class="filter-item">
          <span class="filter-label">操作时间</span>
          <DatePicker
            class="filter-date"
            type="daterange"
            format="yyyy-MM-dd"
            placeholder="选择时间范围"
            :value="log_filter.date_range"
            @on-change="onLogDateChange"
            transfer
          ></DatePicker>
        </div>
      </div>

      <!-- 入仓库存 -->
      <template v-if="activeTab == 'stock'">
        <div class="table-box">
          <table>
            <thead>
              <tr>
                <th>商品信息</th>
                <th>款号</th>
                <th>仓库数量</th>
                <th>占用中</th>
                <th>在途数量</th>
              </tr>
            </thead>
            <tbody v-for="(item, index) in stock_list" :key="`st${index}`">
              <tr>
                <td>
                  <div class="col-info">
                    <div class="product-image">
                      <img :src="item.product_image" alt="" />
                    </div>
                    <div class="text-info">
                      <div class="product-name">{{ item.product_name }}</div>
                    </div>
                  </div>
                </td>
                <td><div class="col-text">{{ item.style_no || '-' }}</div></td>
                <td>
                  <div
                    class="col-num"
                    :class="{ clickable: item.warehouse_num > 0 }"
                    @click="showStockDetail(item, 'warehouse_num')"
                  >
                    {{ item.warehouse_num }}
                  </div>
                </td>
                <!-- 占用中：下单已锁定、尚未被衣创库存同步确认的数量，可售库存 = 仓库数量 - 占用中 -->
                <td><div class="col-num">{{ item.locked_num || 0 }}</div></td>
                <td>
                  <div
                    class="col-num"
                    :class="{ clickable: item.intransit_num > 0 }"
                    @click="showStockDetail(item, 'intransit_num')"
                  >
                    {{ item.intransit_num }}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-if="!stock_list.length" class="empty">暂无数据</div>
        </div>
      </template>

      <!-- 入仓记录 -->
      <template v-else-if="activeTab == 'inbound'">
        <div class="table-box">
          <table>
            <thead>
              <tr>
                <th>入仓单号</th>
                <th>款号</th>
                <th>商品名称</th>
                <th>颜色</th>
                <th>尺码</th>
                <th>寄出数量</th>
                <th>实际入仓数量</th>
                <th>建单日期</th>
                <th>入仓日期</th>
                <th>入仓状态</th>
              </tr>
            </thead>
            <tbody v-for="(item, index) in inbound_list" :key="`in${index}`">
              <tr>
                <td><div class="col-text">{{ item.inbound_no }}</div></td>
                <td><div class="col-text">{{ item.style_no || '-' }}</div></td>
                <td><div class="col-text">{{ item.product_name }}</div></td>
                <td><div class="col-text">{{ item.attr2_name || '-' }}</div></td>
                <td><div class="col-text">{{ item.attr1_name || '-' }}</div></td>
                <td><div class="col-text">{{ item.send_num }}</div></td>
                <td><div class="col-text">{{ item.real_num }}</div></td>
                <td><div class="col-text">{{ formatTime(item.create_time) }}</div></td>
                <td><div class="col-text">{{ item.inbound_time ? formatTime(item.inbound_time) : '-' }}</div></td>
                <td>
                  <!-- 回调异常（回调里对不上的明细 / 未到货规格）可通过悬浮查看留痕 -->
                  <div
                    class="col-status"
                    :class="inboundStatusClass(item)"
                    :title="item.notice_remark || ''"
                  >
                    {{ inboundStatusText(item) }}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-if="!inbound_list.length" class="empty">暂无数据</div>
        </div>
      </template>

      <!-- 出入库明细 -->
      <template v-else>
        <div class="table-box">
          <table>
            <thead>
              <tr>
                <th>出入库单号</th>
                <th>款号</th>
                <th>商品名称</th>
                <th>颜色</th>
                <th>尺码</th>
                <th>数量</th>
                <th>类型</th>
                <th>业务单号</th>
                <th>操作时间</th>
              </tr>
            </thead>
            <tbody v-for="(item, index) in log_list" :key="`lg${index}`">
              <tr>
                <td><div class="col-text">{{ item.log_no }}</div></td>
                <td><div class="col-text">{{ item.style_no || '-' }}</div></td>
                <td><div class="col-text">{{ item.product_name }}</div></td>
                <td><div class="col-text">{{ item.attr2_name || '-' }}</div></td>
                <td><div class="col-text">{{ item.attr1_name || '-' }}</div></td>
                <td><div class="col-text">{{ item.num }}</div></td>
                <td>
                  <div
                    class="col-type"
                    :class="item.type == 2 ? 'out' : 'in'"
                  >
                    {{ item.type_text }}
                  </div>
                </td>
                <td><div class="col-text">{{ item.biz_no || '-' }}</div></td>
                <td><div class="col-text">{{ formatTime(item.create_time) }}</div></td>
              </tr>
            </tbody>
          </table>
          <div v-if="!log_list.length" class="empty">暂无数据</div>
        </div>
      </template>

      <div class="bottom-tool">
        <div></div>
        <div class="page-container">
          <Page
            :total="total_count"
            :current="form_data.page"
            size="small"
            prev-text="上一页"
            next-text="下一页"
            @on-change="onPageChange"
          ></Page>
        </div>
      </div>
    </div>

    <!-- 规格明细弹窗 -->
    <popup :show.sync="is_detail_show" width="520px" @on-close="is_detail_show = false">
      <div class="detail-box">
        <h3 class="detail-title">
          {{ detailType == 'warehouse_num' ? '仓库数量' : '在途数量' }}规格明细
        </h3>
        <div class="detail-sub">{{ currentItem.product_name }}（款号：{{ currentItem.style_no || '-' }}）</div>
        <table class="detail-table">
          <thead>
            <tr>
              <th>颜色</th>
              <th>尺码</th>
              <th>数量</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(s, idx) in stock_detail" :key="`sd${idx}`">
              <td>{{ s.attr2_name || '-' }}</td>
              <td>{{ s.attr1_name || '-' }}</td>
              <td>{{ detailType == 'warehouse_num' ? s.warehouse_num : s.intransit_num }}</td>
            </tr>
          </tbody>
        </table>
        <div v-if="!stock_detail.length" class="empty">暂无规格数据</div>
      </div>
    </popup>

    <!-- 新增入仓弹窗 -->
    <warehouse-inbound
      v-if="is_inbound_show"
      @close="is_inbound_show = false"
      @success="onInboundSuccess"
    />
  </div>
</template>
<script>
import {
  WarehouseStockList,
  WarehouseStockDetail,
  WarehouseInboundList,
  WarehouseLogList,
} from "@/api/index";
import Popup from "@/components/Popup.vue";
import WarehouseInbound from "./WarehouseInbound.vue";
export default {
  name: "WarehouseManage",
  components: {
    Popup,
    WarehouseInbound,
  },
  data() {
    return {
      tabs: [
        { name: "入仓库存", value: "stock" },
        { name: "入仓记录", value: "inbound" },
        { name: "出入库明细", value: "log" },
      ],
      activeTab: "stock",
      form_data: {
        page: 1,
        limit: 15,
        keyword: "",
      },
      stock_list: [],
      inbound_list: [],
      log_list: [],
      total_count: 0,
      log_filter: {
        type: 0,
        date_range: [],
      },
      is_detail_show: false,
      currentItem: {},
      detailType: "warehouse_num",
      stock_detail: [],
      is_inbound_show: false,
    };
  },
  computed: {
    searchPlaceholder() {
      if (this.activeTab == "stock") return "输入款号或商品名称搜索";
      if (this.activeTab == "inbound") return "输入入仓单号/款号/商品名称搜索";
      return "输入出入库单号/业务单号/商品名称搜索";
    },
  },
  mounted() {
    this.getList();
  },
  methods: {
    formatTime(time) {
      if (!time) return "-";
      const d = new Date(time * 1000);
      const pad = (n) => (n < 10 ? "0" + n : n);
      return (
        d.getFullYear() +
        "-" +
        pad(d.getMonth() + 1) +
        "-" +
        pad(d.getDate()) +
        " " +
        pad(d.getHours()) +
        ":" +
        pad(d.getMinutes()) +
        ":" +
        pad(d.getSeconds())
      );
    },
    // 入仓状态文案：已入仓 / 待入仓 / 通知工厂失败（异常时附加提示）
    inboundStatusText(item) {
      if (item.status == 1) {
        return item.notice_remark ? "已入仓（有异常）" : "已入仓";
      }
      if (item.notice_status == 2) return "通知工厂失败";
      return "待入仓";
    },
    inboundStatusClass(item) {
      if (item.status == 1) return item.notice_remark ? "warn" : "success";
      if (item.notice_status == 2) return "danger";
      return "wait";
    },
    switchTab(value) {
      this.activeTab = value;
      this.form_data.page = 1;
      this.form_data.keyword = "";
      this.log_filter = { type: 0, date_range: [] };
      this.getList();
    },
    onSearch() {
      this.form_data.page = 1;
      this.getList();
    },
    onPageChange(page) {
      this.form_data.page = page;
      this.getList();
    },
    // 日期字符串转时间戳（is_end 为真时取当天 23:59:59）
    dateToTimestamp(date_str, is_end) {
      if (!date_str) return "";
      const d = new Date(String(date_str).replace(/-/g, "/"));
      if (isNaN(d.getTime())) return "";
      return Math.floor(d.getTime() / 1000) + (is_end ? 86399 : 0);
    },
    onLogTypeChange(type) {
      this.log_filter.type = type;
      this.form_data.page = 1;
      this.getList();
    },
    onLogDateChange(range) {
      this.log_filter.date_range = range || [];
      this.form_data.page = 1;
      this.getList();
    },
    async getList() {
      const param = { ...this.form_data };
      // 出入库明细附带类型与操作时间范围筛选
      if (this.activeTab == "log") {
        param.type = this.log_filter.type;
        const range = this.log_filter.date_range;
        if (range && range.length === 2) {
          param.start_time = this.dateToTimestamp(range[0], false);
          param.end_time = this.dateToTimestamp(range[1], true);
        }
      }
      try {
        if (this.activeTab == "stock") {
          const { data } = await WarehouseStockList(param);
          this.stock_list = data.list;
          this.total_count = data.total;
        } else if (this.activeTab == "inbound") {
          const { data } = await WarehouseInboundList(param);
          this.inbound_list = data.list;
          this.total_count = data.total;
        } else {
          const { data } = await WarehouseLogList(param);
          this.log_list = data.list;
          this.total_count = data.total;
        }
      } catch (e) {
        console.log("e-warehouse", e);
      }
    },
    async showStockDetail(item, type) {
      if (item[type] <= 0) return;
      this.currentItem = item;
      this.detailType = type;
      try {
        const { data } = await WarehouseStockDetail({ product_id: item.product_id });
        this.stock_detail = data;
        this.is_detail_show = true;
      } catch (e) {
        console.log("e-stock-detail", e);
      }
    },
    showInbound() {
      this.is_inbound_show = true;
    },
    onInboundSuccess() {
      this.is_inbound_show = false;
      this.form_data.page = 1;
      this.getList();
      this.$layer.msg("新增入仓成功");
    },
  },
};
</script>
<style lang="scss" scoped>
.warehouse-manage {
  width: 100%;
  background: #ffffff;
  height: 100%;
  .content-wrapper {
    padding: 30px 30px 0 30px;
    .title {
      width: 100%;
      border-bottom: 1px solid #d6d6d6;
      display: flex;
      align-items: center;
      column-gap: 30px;
      span {
        font-weight: bold;
        font-size: 18px;
        color: #000000;
        padding-bottom: 17px;
        border-bottom: 3px solid #ff5967;
      }
      .add-btn {
        background: #ff5967;
        border-color: #ff5967;
        color: #ffffff;
        border-radius: 20px;
        padding: 5px 20px;
      }
    }
    .tool-wrapper {
      margin-top: 28px;
      display: flex;
      justify-content: space-between;
      .menu-box {
        display: flex;
        align-items: center;
        .menu-item {
          margin: 0 20px;
          &:first-child {
            margin-left: 0;
          }
          cursor: pointer;
          font-size: 14px;
          color: #666666;
          &.active {
            color: #1a1a1a;
            font-weight: bold;
          }
        }
      }
      .search-box {
        background: #f2f3f5;
        border-radius: 20px;
        display: flex;
        align-items: center;
        padding: 10px 20px;
        width: 322px;
        input {
          border: none;
          outline: none;
          background: transparent;
          flex: 1;
          padding-right: 10px;
        }
        .search-btn {
          padding-left: 20px;
          border-left: 1px solid rgba(0, 0, 0, 0.1);
          cursor: pointer;
        }
      }
    }
    .log-filter {
      margin-top: 16px;
      display: flex;
      align-items: center;
      .filter-item {
        display: flex;
        align-items: center;
        margin-right: 32px;
        .filter-label {
          font-size: 13px;
          color: #666666;
          margin-right: 10px;
        }
        .filter-select {
          width: 120px;
        }
        .filter-date {
          width: 260px;
        }
      }
    }
    .table-box {
      margin-top: 20px;
      width: 100%;
      overflow: hidden;
      table {
        width: 100%;
        table-layout: fixed;
        border-collapse: separate;
        border-spacing: 0 10px;
        thead {
          background: #f0f0f0;
          height: 40px;
          tr {
            th {
              text-align: center;
              font-size: 13px;
              color: #666666;
            }
          }
        }
        tbody {
          width: 100%;
          tr {
            width: 100%;
            box-shadow: inset 0 0 0 1px #e6e6e6;
            border-radius: 8px;
            td {
              vertical-align: middle;
              text-align: center;
              padding: 10px 0;
              .col-info {
                display: flex;
                align-items: center;
                .product-image {
                  width: 60px;
                  height: 60px;
                  margin-right: 16px;
                  border: 1px solid #e6e6e6;
                  border-radius: 5px;
                  object-fit: cover;
                  flex-shrink: 0;
                  overflow: hidden;
                  img {
                    width: 100%;
                    height: 100%;
                    border-radius: 5px;
                  }
                }
                .text-info {
                  .product-name {
                    font-size: 14px;
                    color: #333333;
                    font-weight: 500;
                    text-align: left;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                  }
                }
              }
              .col-text {
                font-size: 13px;
                color: #666666;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              }
              .col-num {
                font-size: 14px;
                color: #0d99ff;
                font-weight: 500;
                &.clickable {
                  cursor: pointer;
                  text-decoration: underline;
                }
              }
              .col-type {
                display: inline-block;
                padding: 2px 12px;
                border-radius: 12px;
                font-size: 12px;
                &.in {
                  background: rgba(2, 193, 96, 0.1);
                  color: #02c160;
                }
                &.out {
                  background: rgba(255, 89, 103, 0.1);
                  color: #ff5967;
                }
              }
              .col-status {
                display: inline-block;
                padding: 2px 12px;
                border-radius: 12px;
                font-size: 12px;
                &.success {
                  background: rgba(2, 193, 96, 0.1);
                  color: #02c160;
                }
                &.wait {
                  background: rgba(255, 153, 0, 0.1);
                  color: #ff9900;
                }
                &.danger {
                  background: rgba(255, 89, 103, 0.1);
                  color: #ff5967;
                }
                &.warn {
                  background: rgba(255, 153, 0, 0.1);
                  color: #ff9900;
                  cursor: help;
                }
              }
            }
          }
        }
      }
      .empty {
        text-align: center;
        color: #999999;
        padding: 40px 0;
      }
    }
    .bottom-tool {
      padding-top: 30px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      .page-container {
        height: 35px;
      }
    }
  }
  .detail-box {
    padding: 25px;
    .detail-title {
      font-size: 16px;
      font-weight: 600;
      color: #1a1a1a;
      margin-bottom: 6px;
    }
    .detail-sub {
      font-size: 13px;
      color: #999999;
      margin-bottom: 16px;
    }
    .detail-table {
      width: 100%;
      border-collapse: collapse;
      thead {
        background: #f0f0f0;
        th {
          padding: 10px 0;
          font-size: 13px;
          color: #666666;
          text-align: center;
        }
      }
      tbody {
        tr {
          border-bottom: 1px solid #f0f0f0;
          td {
            padding: 12px 0;
            text-align: center;
            font-size: 13px;
            color: #666666;
          }
        }
      }
    }
    .empty {
      text-align: center;
      color: #999999;
      padding: 30px 0;
    }
  }
}
</style>
